// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.
import React, { useState, useEffect } from "react";
import Header from "../../component/_elements/Header.tsx";
import { useNavigate, useOutletContext } from "react-router-dom";
import { OutletContextType } from "../../types/GlobalType.ts";

import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import {
  studentControler,
  StudentType,
} from "../../_api/controllers/student.controller.ts";
import { parentController } from "../../_api/controllers/parent.controller.ts";
import {
  classeController,
  motifPaiementController,
  promotionController,
  schoolYearController,
} from "../../_api/controllers/scolarite.controller.ts";
import { capitalizeFirstLetter } from "../../_services/utils.jsx";
import { studentAttendanceController } from "../../_api/controllers/attendance.controller.ts";
import { paymentController } from "../../_api/controllers/payment.controller.ts";
import { personalController } from "../../_api/controllers/personal.controller.ts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

// Coordonnées affichées dans l'en-tête des exports (PDF/Excel).
// TODO: remplacer le numéro de téléphone par le vrai contact de l'établissement.
const SCHOOL_INFO = {
  name: "ETPM",
  fullName: "Ecole Technique Professionnelle Des Metiers",
  telephone: "+243810458708",
};

// skeleton component for students management loading state
const StudentsManagementSkeleton: React.FC<{
  isDarkMode: boolean;
}> = ({ isDarkMode }) => (
  <main className="ml-20 md:ml-0 p-6 mt-16 animate-pulse">
    {/* Header */}
    <div className="mb-6 flex justify-between items-center">
      <div>
        <div
          className={`h-6 w-56 rounded mb-2 ${
            isDarkMode ? "bg-gray-700" : "bg-gray-300"
          }`}
        ></div>
        <div
          className={`h-4 sm:w-80 rounded ${
            isDarkMode ? "bg-gray-600" : "bg-gray-200"
          }`}
        ></div>
      </div>

      <div className="flex gap-3">
        <div
          className={`h-10 w-44 rounded-md ${
            isDarkMode ? "bg-gray-700" : "bg-gray-300"
          }`}
        ></div>
      </div>
    </div>

    {/* Search & Filters */}
    <div className="flex flex-wrap gap-4 mb-6">
      <div
        className={`flex-grow h-10 rounded-md ${
          isDarkMode ? "bg-gray-700" : "bg-gray-300"
        }`}
      ></div>
      <div
        className={`h-10 w-48 rounded-md ${
          isDarkMode ? "bg-gray-700" : "bg-gray-300"
        }`}
      ></div>
      <div
        className={`h-10 w-48 rounded-md ${
          isDarkMode ? "bg-gray-700" : "bg-gray-300"
        }`}
      ></div>
    </div>

    {/* TABLE SKELETON */}
    <div
      className={`rounded-lg overflow-hidden border mb-6 ${
        isDarkMode ? "border-gray-700" : "border-gray-300"
      }`}
    >
      <div className="w-full overflow-x-auto">
        <table className="min-w-full">
          <thead className={isDarkMode ? "bg-[#161B22]" : "bg-gray-200"}>
            <tr>
              {[...Array(7)].map((_, i) => (
                <th key={i} className="px-6 py-4">
                  <div
                    className={`h-4 rounded w-full ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-300"
                    }`}
                  ></div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody
            className={`divide-y ${
              isDarkMode
                ? "bg-[#0D1117] divide-gray-700"
                : "bg-white divide-gray-200"
            }`}
          >
            {[...Array(10)].map((_, row) => (
              <tr key={row}>
                {[...Array(7)].map((_, col) => (
                  <td key={col} className="px-6 py-4">
                    <div
                      className={`h-4 rounded w-full ${
                        isDarkMode ? "bg-gray-700" : "bg-gray-200"
                      }`}
                    ></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </main>
);

const Student: React.FC = () => {
  const { isDarkMode, setIsDarkMode } = useOutletContext<OutletContextType>();
  const [currentYear, setCurrentYear] = useState("2025 - 2026");
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClass, setSelectedClass] = useState("Toutes les classes");
  const [selectedStatus, setSelectedStatus] = useState<any>("Tous les statuts");
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [viewMode, setViewMode] = useState("liste");
  // const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const studentsPerPage = 100;

  const handleNavigateToStudentDetails = (student: any) => {
    // console.log(student);
    navigate(`/eleves/${student.id}`, {
      state: student, // 👈 pass data here
    });
  };

  const navigate = useNavigate();

  const { data: eleveData, isLoading: isEleveLoading } = useQuery({
    queryKey: ["students"],
    queryFn: studentControler.getAll,
  });

  //sclarité request
  const { data: parentsData, isLoading: isParentsLoading } = useQuery({
    queryKey: ["parents"],
    queryFn: parentController.getAll,
  });

  //fetch promotion
  const { data: promotionsData, isLoading: isPromotionsLoading } = useQuery({
    queryKey: ["promotions"],
    queryFn: promotionController.getAll,
  });

  //fetch classe
  const { data: dataClasseObj, isLoading: isClassesLoading } = useQuery({
    queryKey: ["classes"],
    queryFn: classeController.getAll,
  });

  //fetch anne scolaire
  const { data: schoolYear } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });

  const schoolYearArr = schoolYear?.data || [];
  const selectedSchoolYear = schoolYearArr.find((sy: any) => sy.selected);

  // fetch Active schoolYear
  useQuery({
    queryKey: ["ActiveSchoolYear"],
    queryFn: schoolYearController.getActive,
  });

  // fetch personals
  useQuery({
    queryKey: ["personals"],
    queryFn: personalController.getAll,
  });

  // fetch motif Paiement
  useQuery({
    queryKey: ["motifPaiement"],
    queryFn: motifPaiementController.getAll,
  });

  //fetch attendance of the current month
  const date = moment().format("YYYY-MM-DD");
  useQuery({
    queryKey: ["studentAttendance"],
    queryFn: () => studentAttendanceController.getAllByMonth(date),
  });

  //fetch paiement of current student of cactive school year
  useQuery({
    queryKey: ["payments"],
    queryFn: paymentController.getAll,
  });

  const dataClasses = dataClasseObj?.data ? dataClasseObj.data : [];
  const dataParentArr = parentsData?.data ? parentsData.data : [];
  const dataPromotionArr = promotionsData?.data ? promotionsData.data : [];

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  const toggleYearDropdown = () => {
    setIsYearDropdownOpen(!isYearDropdownOpen);
  };
  const toggleClassDropdown = () => {
    setIsClassDropdownOpen(!isClassDropdownOpen);
    if (isClassDropdownOpen) setIsStatusDropdownOpen(false);
  };
  const toggleStatusDropdown = () => {
    setIsStatusDropdownOpen(!isStatusDropdownOpen);
    if (isStatusDropdownOpen) setIsClassDropdownOpen(false);
  };

  const toggleExportDropdown = () => {
    setIsExportDropdownOpen(!isExportDropdownOpen);
  };

  // Affiche une notification éphémère en bas à droite de l'écran
  const showExportNotification = (
    message: string,
    type: "success" | "erreur",
  ) => {
    const notification = document.createElement("div");
    notification.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white ${
      type === "success"
        ? isDarkMode
          ? "bg-green-600"
          : "bg-green-500"
        : isDarkMode
          ? "bg-red-600"
          : "bg-red-500"
    } shadow-lg transition-opacity duration-500`;
    notification.innerHTML = `<i class="fas ${
      type === "success" ? "fa-check-circle" : "fa-circle-exclamation"
    } mr-2"></i>${message}`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.opacity = "0";
      setTimeout(() => notification.remove(), 500);
    }, 3000);
  };

  // Titre du document, basé sur le filtre classe + l'année scolaire actives
  const getExportTitle = () => {
    const classeLabel = selectedClass;
    const anneeLabel = selectedSchoolYear?.designation || currentYear;
    return `Liste des élèves — ${classeLabel} — ${anneeLabel}`;
  };

  // Lignes formatées à partir de TOUS les élèves correspondant au filtre actif
  // (indépendamment de la pagination), dans l'ordre des colonnes demandées.
  const getExportRows = () =>
    filteredStudents
      .sort((a, b) => a.nom.toLowerCase().localeCompare(b.nom.toLowerCase()))
      .map((student: any, index: number) => [
        `${index + 1} `,
        `${(student.nom || "").toUpperCase()} ${(
          student.postnom || ""
        ).toUpperCase()} ${capitalizeFirstLetter(student.prenom || "")}`,
        student.classeDesignation || "-",
        student.sexe === "F" ? "Féminin" : "Masculin",
        student.telephone || "-",
        student.date_naissance
          ? moment(student.date_naissance).format("DD/MM/YYYY")
          : "-",
      ]);

  // Dessine l'en-tête professionnel commun (logo textuel + coordonnées) sur le PDF
  const drawPdfHeader = (doc: jsPDF) => {
    const pageWidth = doc.internal.pageSize.getWidth();

    // Logo textuel "ETPM" à gauche + sigle en dessous
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(37, 99, 235); // bleu
    doc.text(SCHOOL_INFO.name, 14, 18);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(SCHOOL_INFO.fullName, 14, 24);

    // Coordonnées à droite
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(`Tél: ${SCHOOL_INFO.telephone}`, pageWidth - 14, 18, {
      align: "right",
    });

    // Ligne de séparation sous l'en-tête
    doc.setDrawColor(210, 210, 210);
    doc.line(14, 28, pageWidth - 14, 28);

    // Titre du document
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(getExportTitle(), pageWidth / 2, 38, { align: "center" });

    return 44; // position Y de départ pour le contenu suivant
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const startY = drawPdfHeader(doc);

    autoTable(doc, {
      startY,
      head: [["N°", "Nom", "Classe", "Genre", "Contact", "Date de naissance"]],
      body: getExportRows(),
      theme: "striped",
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: 255,
        fontStyle: "bold",
      },
      styles: { fontSize: 9, cellPadding: 3 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { left: 14, right: 14 },
    });

    // Pied de page : date de génération + numérotation, sur chaque page
    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Généré le ${moment().format("DD/MM/YYYY à HH:mm")} - ChikSoft`,
        14,
        pageHeight - 8,
      );
      doc.text(`Page ${i} / ${pageCount}`, pageWidth - 14, pageHeight - 8, {
        align: "right",
      });
    }

    doc.save(`liste_eleves_${moment().format("YYYY-MM-DD")}.pdf`);
  };

  const exportToExcel = () => {
    const rows = filteredStudents.map((student: any, index: number) => ({
      Nom: `${(student.nom || "").toUpperCase()} ${(
        student.postnom || ""
      ).toUpperCase()} ${capitalizeFirstLetter(student.prenom || "")}`,
      Classe: student.classeDesignation || "-",
      Genre: student.sexe === "F" ? "Féminin" : "Masculin",
      Contact: student.telephone || "-",
      "Date de naissance": student.date_naissance
        ? moment(student.date_naissance).format("DD/MM/YYYY")
        : "-",
    }));

    // En-tête professionnel sous forme de lignes fusionnées au-dessus du tableau
    const headerRows = [
      [SCHOOL_INFO.name],
      [SCHOOL_INFO.fullName],
      [`Tél: ${SCHOOL_INFO.telephone}`],
      [],
      [getExportTitle()],
      [],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(headerRows);
    XLSX.utils.sheet_add_json(worksheet, rows, {
      origin: headerRows.length,
    });

    worksheet["!cols"] = [
      { wch: 32 },
      { wch: 18 },
      { wch: 12 },
      { wch: 16 },
      { wch: 16 },
    ];
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: 4 } },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Élèves");
    XLSX.writeFile(
      workbook,
      `liste_eleves_${moment().format("YYYY-MM-DD")}.xlsx`,
    );
  };

  const exportData = (format: "PDF" | "Excel") => {
    if (filteredStudents.length === 0) {
      showExportNotification(
        "Aucun élève à exporter pour ce filtre.",
        "erreur",
      );
      setIsExportDropdownOpen(false);
      return;
    }

    try {
      if (format === "PDF") {
        exportToPDF();
      } else {
        exportToExcel();
      }
      showExportNotification(
        format === "PDF" ? "Export PDF réussi" : "Export Excel réussi",
        "success",
      );
    } catch (err) {
      showExportNotification("L'export a échoué, réessayez.", "erreur");
    }

    setIsExportDropdownOpen(false);
  };
  // Données des élèves
  const parentDataArr = eleveData?.data ? eleveData.data : [];

  //Create explicite dataArray for students
  let updatedStudents: StudentType[] = parentDataArr.map((student) => {
    let parentData: any,
      promotionData: any,
      classeDesignation: any,
      classeId: any;

    for (const parent of dataParentArr) {
      if (parent.parent_id === student.parent_id) {
        parentData = parent;
      }
    }
    for (const promotion of dataPromotionArr) {
      if (
        promotion.annee_scolaire_id === selectedSchoolYear?.id &&
        promotion.eleve_id === student.eleve_id
      ) {
        promotionData = promotion;
        //it is possible to get all promotion of the current student here by handling the condition above
      }
    }
    for (const classe of dataClasses) {
      if (promotionData?.classe_id === classe.id) {
        classeDesignation = classe.designation;
        classeId = classe.id;
      }
    }

    return {
      ...student,
      parentData: parentData,
      promotionData: promotionData,
      classeDesignation: classeDesignation,
      classeId: classeId,
    };
  });

  //filter out students without promotion in the selected school year
  updatedStudents = updatedStudents.filter((student) => student.promotionData);

  const students = [...updatedStudents];
  // console.log(students);

  // Filtrer les élèves selon la recherche et les filtres
  const filteredStudents = students.filter((student: any) => {
    const matchesSearch =
      student?.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.prenom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.postnom?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesClass =
      selectedClass === "Toutes les classes" ||
      student.classeDesignation?.toLowerCase() === selectedClass.toLowerCase();

    const matchesStatus =
      selectedStatus === "Tous les statuts" ||
      student.status === selectedStatus;
    return matchesSearch && matchesClass && matchesStatus;
  });
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent,
  );
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const handlePageChange = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  //Handle mouse click outside for dropdowns
  const ExportDropdownRef = React.useRef<HTMLDivElement>(null);
  const ClassDropdownRef = React.useRef<HTMLDivElement>(null);
  const StatusDropdownRef = React.useRef<HTMLDivElement>(null);
  // Fermeture des menus lors d'un clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //export button
      if (
        ExportDropdownRef.current &&
        !ExportDropdownRef.current.contains(event.target as Node)
      ) {
        setIsExportDropdownOpen(false);
      }

      //class button
      if (
        ClassDropdownRef.current &&
        !ClassDropdownRef.current.contains(event.target as Node)
      ) {
        setIsClassDropdownOpen(false);
      }

      //status button
      if (
        StatusDropdownRef.current &&
        !StatusDropdownRef.current.contains(event.target as Node)
      ) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Styles communs réutilisés pour une cohérence visuelle
  const filterButtonClass = `flex items-center px-4 py-2.5 rounded-lg text-sm ${
    isDarkMode
      ? "bg-[#161B22] hover:bg-gray-700 text-gray-300 border-gray-700"
      : "bg-white hover:bg-gray-50 text-gray-700 border-gray-300"
  } border !rounded-button whitespace-nowrap`;

  const dropdownPanelClass = `absolute top-full right-0 mt-1.5 w-48 rounded-lg shadow-lg overflow-hidden ${
    isDarkMode ? "bg-[#161B22]" : "bg-white"
  } z-10 border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`;

  const dropdownItemClass = `flex items-center w-full px-4 py-2.5 text-xs text-left ${
    isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
  } !rounded-button whitespace-nowrap`;

  const statusBadgeClass = (active: boolean) =>
    `inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px]
   font-semibold rounded-full ${
     active
       ? isDarkMode
         ? "bg-emerald-500/10 text-emerald-400"
         : "bg-emerald-50 text-emerald-600"
       : isDarkMode
         ? "bg-red-500/10 text-red-400"
         : "bg-red-50 text-red-600"
   }`;

  return (
    <div className="md:ml-60 md:w-[calc(100%-15rem)]">
      {/* Header */}
      <Header
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        toggleDarkMode={toggleDarkMode}
        currentYear={currentYear}
        isYearDropdownOpen={isYearDropdownOpen}
        toggleYearDropdown={toggleYearDropdown}
        setCurrentYear={setCurrentYear}
        setIsYearDropdownOpen={setIsYearDropdownOpen}
      />
      {/* Students Management Content */}

      {isEleveLoading ||
      isParentsLoading ||
      isPromotionsLoading ||
      isClassesLoading ? (
        <StudentsManagementSkeleton isDarkMode={isDarkMode} />
      ) : (
        <main className="ml-20 md:ml-0 p-6 mt-16">
          <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Titre */}
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  isDarkMode
                    ? "bg-blue-500/10 text-blue-400"
                    : "bg-blue-50 text-blue-600"
                }`}
              >
                <i className="fas fa-user-graduate text-sm" />
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-base font-semibold leading-tight">
                    Gestion des élèves
                  </h1>

                  <span
                    className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      isDarkMode
                        ? "bg-[#21262D] text-gray-400"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {filteredStudents.length} élève
                    {filteredStudents.length > 1 ? "s" : ""}
                  </span>
                </div>

                <p
                  className={`text-[11px] mt-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Consultez et gérez les dossiers des élèves
                </p>
              </div>
            </div>

            {/* Export */}
            <div className="relative" ref={ExportDropdownRef}>
              <button
                onClick={toggleExportDropdown}
                className={`h-9 px-3.5 rounded-lg border flex items-center gap-2
        text-xs font-medium transition-colors ${
          isDarkMode
            ? "bg-[#0D1117] border-[#30363D] hover:bg-[#161B22] text-gray-300"
            : "bg-white border-gray-200 hover:bg-gray-50 text-gray-600 shadow-sm"
        }`}
              >
                <i className="fas fa-download text-[11px]" />
                Exporter
                <i
                  className={`fas fa-chevron-down text-[9px] ml-1 transition-transform ${
                    isExportDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isExportDropdownOpen && (
                <div
                  className={`absolute right-0 top-full mt-2 w-48 rounded-xl border
          shadow-xl overflow-hidden z-30 ${
            isDarkMode
              ? "bg-[#161B22] border-[#30363D]"
              : "bg-white border-gray-200"
          }`}
                >
                  <div className="p-1.5">
                    <button
                      onClick={() => exportData("PDF")}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              text-xs text-left transition ${
                isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
              }`}
                    >
                      <div className="w-7 h-7 rounded-md bg-red-500/10 text-red-500 flex items-center justify-center">
                        <i className="fas fa-file-pdf text-xs" />
                      </div>

                      <div>
                        <p className="font-medium">Format PDF</p>
                        <p className="text-[9px] text-gray-500 mt-0.5">
                          Document imprimable
                        </p>
                      </div>
                    </button>

                    <button
                      onClick={() => exportData("Excel")}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              text-xs text-left transition ${
                isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
              }`}
                    >
                      <div className="w-7 h-7 rounded-md bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                        <i className="fas fa-file-excel text-xs" />
                      </div>

                      <div>
                        <p className="font-medium">Format Excel</p>
                        <p className="text-[9px] text-gray-500 mt-0.5">
                          Feuille de calcul
                        </p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Search and Filters */}
          <div
            className={`mb-5 rounded-xl border p-3 ${
              isDarkMode
                ? "bg-[#0D1117] border-[#30363D]"
                : "bg-white border-gray-200 shadow-sm"
            }`}
          >
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              {/* Recherche */}
              <div className="relative flex-1">
                <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs" />

                <input
                  type="text"
                  placeholder="Rechercher un élève..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full h-10 pl-9 pr-4 rounded-lg border text-xs
          outline-none transition ${
            isDarkMode
              ? "bg-[#161B22] border-[#30363D] placeholder:text-gray-600 focus:border-blue-500"
              : "bg-gray-50 border-gray-200 placeholder:text-gray-400 focus:bg-white focus:border-blue-500"
          }`}
                />
              </div>

              <div className="flex items-center flex-wrap gap-2">
                {/* CLASSE */}
                <div className="relative" ref={ClassDropdownRef}>
                  <button
                    onClick={toggleClassDropdown}
                    className={`h-10 px-3.5 rounded-lg border flex items-center gap-2
            text-xs font-medium transition ${
              isDarkMode
                ? "bg-[#161B22] border-[#30363D] hover:bg-[#21262D]"
                : "bg-white border-gray-200 hover:bg-gray-50"
            }`}
                  >
                    <i className="fas fa-school text-blue-500 text-[11px]" />

                    <span className="max-w-[130px] truncate">
                      {selectedClass}
                    </span>

                    <i
                      className={`fas fa-chevron-down text-[9px] text-gray-500 transition ${
                        isClassDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isClassDropdownOpen && (
                    <div
                      className={`absolute right-0 lg:left-0 lg:right-auto top-full mt-2
              min-w-[210px] max-h-72 overflow-y-auto rounded-xl border shadow-xl
              z-30 p-1.5 ${
                isDarkMode
                  ? "bg-[#161B22] border-[#30363D]"
                  : "bg-white border-gray-200"
              }`}
                    >
                      <button
                        onClick={() => {
                          setSelectedClass("Toutes les classes");
                          setIsClassDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2.5
                rounded-lg text-xs text-left transition ${
                  isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
                }`}
                      >
                        <i className="fas fa-layer-group text-gray-500 w-4" />
                        Toutes les classes
                      </button>

                      {dataClasses.map((classe) => (
                        <button
                          key={classe.id}
                          onClick={() => {
                            setSelectedClass(classe.designation || "");
                            setIsClassDropdownOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2.5
                  rounded-lg text-xs text-left transition ${
                    selectedClass === classe.designation
                      ? isDarkMode
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-blue-50 text-blue-600"
                      : isDarkMode
                        ? "hover:bg-[#21262D]"
                        : "hover:bg-gray-50"
                  }`}
                        >
                          <i className="fas fa-chalkboard text-[10px] w-4" />
                          {classe.designation}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* STATUT */}
                <div className="relative" ref={StatusDropdownRef}>
                  <button
                    onClick={toggleStatusDropdown}
                    className={`h-10 px-3.5 rounded-lg border flex items-center gap-2
            text-xs font-medium transition ${
              isDarkMode
                ? "bg-[#161B22] border-[#30363D] hover:bg-[#21262D]"
                : "bg-white border-gray-200 hover:bg-gray-50"
            }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        selectedStatus === 1
                          ? "bg-emerald-500"
                          : selectedStatus === 0
                            ? "bg-red-500"
                            : "bg-gray-400"
                      }`}
                    />

                    {selectedStatus === 1
                      ? "Actifs"
                      : selectedStatus === 0
                        ? "Inactifs"
                        : "Tous les statuts"}

                    <i className="fas fa-chevron-down text-[9px] text-gray-500" />
                  </button>

                  {isStatusDropdownOpen && (
                    <div
                      className={`absolute right-0 top-full mt-2 w-44 rounded-xl border
              shadow-xl z-30 p-1.5 ${
                isDarkMode
                  ? "bg-[#161B22] border-[#30363D]"
                  : "bg-white border-gray-200"
              }`}
                    >
                      {[
                        {
                          label: "Tous les statuts",
                          value: "Tous les statuts",
                          color: "bg-gray-400",
                        },
                        {
                          label: "Actifs",
                          value: 1,
                          color: "bg-emerald-500",
                        },
                        {
                          label: "Inactifs",
                          value: 0,
                          color: "bg-red-500",
                        },
                      ].map((status) => (
                        <button
                          key={status.label}
                          onClick={() => {
                            setSelectedStatus(status.value);
                            setIsStatusDropdownOpen(false);
                          }}
                          className={`w-full px-3 py-2.5 rounded-lg text-xs
                  flex items-center gap-2 transition ${
                    isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
                  }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${status.color}`}
                          />
                          {status.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Séparateur */}
                <div
                  className={`hidden lg:block w-px h-6 mx-1 ${
                    isDarkMode ? "bg-[#30363D]" : "bg-gray-200"
                  }`}
                />

                {/* VIEW MODE */}
                <div
                  className={`flex p-1 rounded-lg ${
                    isDarkMode ? "bg-[#161B22]" : "bg-gray-100"
                  }`}
                >
                  <button
                    onClick={() => setViewMode("liste")}
                    title="Vue liste"
                    className={`w-8 h-8 rounded-md flex items-center justify-center transition ${
                      viewMode === "liste"
                        ? isDarkMode
                          ? "bg-[#30363D] text-white shadow-sm"
                          : "bg-white text-blue-600 shadow-sm"
                        : "text-gray-500"
                    }`}
                  >
                    <i className="fas fa-list text-xs" />
                  </button>

                  <button
                    onClick={() => setViewMode("cartes")}
                    title="Vue cartes"
                    className={`w-8 h-8 rounded-md flex items-center justify-center transition ${
                      viewMode === "cartes"
                        ? isDarkMode
                          ? "bg-[#30363D] text-white shadow-sm"
                          : "bg-white text-blue-600 shadow-sm"
                        : "text-gray-500"
                    }`}
                  >
                    <i className="fas fa-th-large text-xs" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Students List View */}
          {viewMode === "liste" && (
            <div
              className={`w-full min-w-0 rounded-xl border overflow-hidden ${
                isDarkMode
                  ? "bg-[#0D1117] border-[#30363D]"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <div className="w-full overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                <table className="w-full table-auto sm:min-w-[480px] md:min-w-[640px]">
                  <thead
                    className={isDarkMode ? "bg-[#161B22]" : "bg-gray-50/80"}
                  >
                    <tr>
                      <th
                        scope="col"
                        className={`px-4 md:px-5 py-3 text-left text-[10px]
                                  font-semibold uppercase tracking-wider ${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                      >
                        Nom
                      </th>
                      <th
                        scope="col"
                        className={`px-4 md:px-5 py-3 text-center text-[10px]
                                  font-semibold uppercase tracking-wider ${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                      >
                        Classe
                      </th>
                      <th
                        scope="col"
                        className={`px-4 md:px-5 py-3 text-left text-[10px]
                                    font-semibold uppercase tracking-wider ${
                                      isDarkMode
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                      >
                        Genre
                      </th>
                      <th
                        scope="col"
                        className={`px-4 md:px-5 py-3 text-left text-[10px]
                            font-semibold uppercase tracking-wider ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                      >
                        Contact
                      </th>
                      <th
                        scope="col"
                        className={`px-4 md:px-5 py-3 text-left text-[10px]
                              font-semibold uppercase tracking-wider ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                      >
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={`divide-y ${
                      isDarkMode
                        ? "bg-[#0D1117] divide-[#21262D]"
                        : "bg-white divide-gray-100"
                    }`}
                  >
                    {currentStudents && currentStudents.length > 0 ? (
                      currentStudents
                        .sort((a, b) =>
                          a.nom
                            .toLowerCase()
                            .localeCompare(b.nom.toLowerCase()),
                        )
                        .map((student) => {
                          return (
                            <tr
                              key={student.eleve_id}
                              className={`group align-middle cursor-pointer transition-colors ${
                                isDarkMode
                                  ? "hover:bg-[#161B22]"
                                  : "hover:bg-blue-50/30"
                              }`}
                              onClick={() =>
                                handleNavigateToStudentDetails(student)
                              }
                            >
                              <td className="px-4 py-3.5 md:px-5">
                                <div className="flex items-center min-w-0">
                                  <div
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center
                                              flex-shrink-0 text-[11px] font-semibold ${
                                                isDarkMode
                                                  ? "bg-blue-500/10 text-blue-400"
                                                  : "bg-blue-50 text-blue-600"
                                              }`}
                                  >
                                    {student.nom
                                      .split(" ")
                                      .map((n: any) => n[0])
                                      .join("")}
                                  </div>

                                  <div className="ml-3 min-w-0">
                                    <p
                                      className={`truncate text-xs font-semibold md:text-sm ${
                                        isDarkMode
                                          ? "text-gray-200"
                                          : "text-gray-800"
                                      }`}
                                    >
                                      {`${student.nom} ${student.postnom} ${capitalizeFirstLetter(
                                        student.prenom,
                                      )}`}
                                    </p>

                                    {/* Garde ici tes badges responsive actuels */}
                                  </div>
                                </div>
                              </td>
                              <td className="hidden px-3 whitespace-nowrap text-center sm:table-cell md:px-6">
                                <div className="flex justify-center min-w-0">
                                  <span
                                    className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1
                                              text-[10px] font-medium ${
                                                isDarkMode
                                                  ? "bg-blue-500/10 text-blue-400"
                                                  : "bg-blue-50 text-blue-600"
                                              }`}
                                  >
                                    <i className="fas fa-graduation-cap text-[9px]" />

                                    {student.classeDesignation || "-"}
                                  </span>
                                </div>
                              </td>
                              <td className="hidden px-3 whitespace-nowrap text-center md:table-cell md:px-6">
                                <div className="flex justify-center">
                                  <span
                                    className={`inline-flex items-center justify-center w-7 h-7 rounded-lg
                                          text-[10px] font-semibold ${
                                            isDarkMode
                                              ? "bg-[#21262D] text-gray-300"
                                              : "bg-gray-100 text-gray-600"
                                          }`}
                                  >
                                    {student.sexe === "F" ? "F" : "M"}
                                  </span>
                                </div>
                              </td>
                              <td className="hidden px-3 whitespace-nowrap lg:table-cell md:px-6">
                                <div
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                  } text-xs font-medium`}
                                >
                                  {student.telephone}
                                </div>
                              </td>
                              <td className="hidden px-3 whitespace-nowrap md:table-cell md:px-6">
                                <span
                                  className={statusBadgeClass(!!student.status)}
                                >
                                  <span
                                    className={`h-1.5 w-1.5 rounded-full ${
                                      student.status
                                        ? "bg-green-500/80"
                                        : "bg-red-500/80"
                                    }`}
                                  ></span>
                                  {student.status ? "Actif" : "Inactif"}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className={`px-6 py-14 text-center ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <i className="fas fa-user-slash text-2xl mb-2 block opacity-40"></i>
                          <span className="text-sm lowercase">
                            Aucun élève trouvé pour l'année scolaire
                            sélectionnée.
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Students Cards View */}
          {viewMode === "cartes" &&
            (currentStudents && currentStudents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {currentStudents.map((student) => {
                  return (
                    <div
                      key={student.id}
                      onClick={() => handleNavigateToStudentDetails(student)}
                      className={`group rounded-xl border overflow-hidden cursor-pointer
    transition-all duration-200 ${
      isDarkMode
        ? "bg-[#0D1117] border-[#30363D] hover:border-[#484F58]"
        : "bg-white border-gray-200 hover:border-blue-200 hover:shadow-md"
    }`}
                    >
                      <div className="p-5">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3 mb-5">
                          <div
                            className={`w-11 h-11 rounded-xl flex items-center justify-center
          text-sm font-bold ${
            isDarkMode
              ? "bg-blue-500/10 text-blue-400"
              : "bg-blue-50 text-blue-600"
          }`}
                          >
                            {student.nom?.charAt(0)}
                            {student.prenom?.charAt(0)}
                          </div>

                          <span className={statusBadgeClass(!!student.status)}>
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                student.status ? "bg-emerald-500" : "bg-red-500"
                              }`}
                            />
                            {student.status ? "Actif" : "Inactif"}
                          </span>
                        </div>

                        {/* Identité */}
                        <div className="mb-5">
                          <h3
                            className="font-semibold text-sm truncate"
                            title={`${student.nom} ${student.postnom} ${student.prenom}`}
                          >
                            {student.nom.toUpperCase()}{" "}
                            {student.postnom.toUpperCase()}{" "}
                            {capitalizeFirstLetter(student.prenom)}
                          </h3>

                          <div className="mt-2">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-1
            rounded-md text-[10px] font-medium ${
              isDarkMode
                ? "bg-blue-500/10 text-blue-400"
                : "bg-blue-50 text-blue-600"
            }`}
                            >
                              <i className="fas fa-graduation-cap text-[9px]" />
                              {student.classeDesignation || "Non affecté"}
                            </span>
                          </div>
                        </div>

                        {/* Informations */}
                        <div
                          className={`space-y-3 pt-4 border-t ${
                            isDarkMode ? "border-[#21262D]" : "border-gray-100"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <i className="fas fa-calendar-alt text-gray-500 text-[10px] w-4" />

                            <div className="flex-1 flex justify-between gap-3">
                              <span className="text-[11px] text-gray-500">
                                Naissance
                              </span>

                              <span className="text-[11px] font-medium">
                                {moment(student.date_naissance).format(
                                  "DD/MM/YYYY",
                                )}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <i className="fas fa-user-friends text-gray-500 text-[10px] w-4" />

                            <div className="flex-1 flex justify-between gap-3 min-w-0">
                              <span className="text-[11px] text-gray-500">
                                Parent
                              </span>

                              <span className="text-[11px] font-medium truncate">
                                {student.parentData?.nom ||
                                student.parentData?.postnom ||
                                student.parentData?.prenom
                                  ? `${student.parentData?.nom} ${capitalizeFirstLetter(
                                      student.parentData?.prenom,
                                    )}`
                                  : "Non renseigné"}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <i className="fas fa-phone text-gray-500 text-[10px] w-4" />

                            <div className="flex-1 flex justify-between gap-3">
                              <span className="text-[11px] text-gray-500">
                                Contact
                              </span>

                              <span className="text-[11px] font-medium">
                                {student.telephone || "Non renseigné"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div
                        className={`px-5 py-3 border-t flex items-center justify-between
      transition-colors ${
        isDarkMode
          ? "border-[#21262D] group-hover:bg-[#161B22]"
          : "border-gray-100 group-hover:bg-gray-50"
      }`}
                      >
                        <span className="text-[11px] font-medium text-blue-500">
                          Voir le dossier
                        </span>

                        <i className="fas fa-arrow-right text-[10px] text-blue-500 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                className={`px-6 py-14 text-center text-sm font-medium border rounded-lg ${
                  isDarkMode
                    ? "text-white border-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
              >
                <i className="fas fa-user-slash text-2xl mb-2 block opacity-40"></i>
                <span className="text-sm lowercase">
                  Aucun élève affecté pour l'année scolaire sélectionnée.
                </span>
              </div>
            ))}
          {/* Pagination */}
          <div
            className={`mt-4 px-1 py-3 flex flex-col sm:flex-row
    sm:items-center sm:justify-between gap-3`}
          >
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                  isDarkMode
                    ? "bg-[#161B22] text-gray-500"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                <i className="fas fa-users text-[10px]" />
              </div>

              <p className="text-[11px] text-gray-500">
                <span className="font-semibold">{filteredStudents.length}</span>{" "}
                élève{filteredStudents.length > 1 ? "s" : ""}
                <span className="hidden sm:inline">
                  {" "}
                  · Affichage {indexOfFirstStudent + 1}–
                  {Math.min(indexOfLastStudent, filteredStudents.length)}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`w-8 h-8 rounded-lg border flex items-center justify-center
        transition ${
          isDarkMode
            ? "border-[#30363D] hover:bg-[#161B22]"
            : "border-gray-200 hover:bg-gray-50"
        } ${currentPage === 1 ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <i className="fas fa-chevron-left text-[9px]" />
              </button>

              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`min-w-8 h-8 px-2 rounded-lg text-[11px]
          font-semibold transition ${
            currentPage === index + 1
              ? "bg-blue-600 text-white shadow-sm"
              : isDarkMode
                ? "border border-[#30363D] hover:bg-[#161B22]"
                : "border border-gray-200 hover:bg-gray-50"
          }`}
                >
                  {index + 1}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`w-8 h-8 rounded-lg border flex items-center justify-center
        transition ${
          isDarkMode
            ? "border-[#30363D] hover:bg-[#161B22]"
            : "border-gray-200 hover:bg-gray-50"
        } ${currentPage === totalPages ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <i className="fas fa-chevron-right text-[9px]" />
              </button>
            </div>
          </div>
        </main>
      )}
    </div>
  );
};
export default Student;
